import { ProjectionAssumptions } from "@components/Trajectory";
import { Button, CardBackground, Radio, Typography } from "@hellocarbo/lasagne";
import { Check, Edit2 } from "lucide-react";
import { SyntheticEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  Form,
  Link,
  redirect,
  useActionData,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useSubmit,
} from "react-router";
import invariant from "tiny-invariant";

import {
  TTrackingCampaignDetails,
  TTrajectory,
  TTrajectoryStep,
} from "@/types";
import {
  getDefaultProjectionYear,
  getEarlyProjectionYear,
  LATE_PROJECTION_YEAR,
  sortTrajectoryStepsByYear,
} from "@/utils/trajectory";

export async function loader() {
  // Get the trajectory information (there is only one trajectory for now)
  const trajectoryResponse = await fetch("/api/client/trajectories");

  if (![200, 201].includes(trajectoryResponse.status)) {
    return { error: true };
  }

  const { data: trajectoryData } = (await trajectoryResponse.json()) as {
    data: TTrajectory;
  };

  // We fetch the tracking campaign linked to the trajectory
  const trackingCampaignResponse = await fetch(
    `/api/client/tracking_campaigns/${trajectoryData.tracking_campaign_id}`
  );

  if (trackingCampaignResponse.status !== 200) {
    return { error: true };
  }

  const { data: trackingCampaignData } =
    (await trackingCampaignResponse.json()) as {
      data: TTrackingCampaignDetails;
    };

  // We fetch the trajectory steps
  const trajectoryStepsResponse = await fetch("/api/client/trajectory_steps");

  if (trajectoryStepsResponse.status !== 200) {
    return { error: true };
  }

  const { data: trajectoryStepsData } =
    (await trajectoryStepsResponse.json()) as { data: Array<TTrajectoryStep> };

  // We sort the trajectory steps by year
  const trajectorySteps = sortTrajectoryStepsByYear(trajectoryStepsData);

  return {
    trackingCampaign: trackingCampaignData,
    trajectory: trajectoryData,
    trajectorySteps,
  };
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const stepsData = {} as Record<string, Record<string, number>>;
  const isTrajectoryCreation = formData.get("isTrajectoryCreation");
  formData.delete("isTrajectoryCreation");

  const zeroValueFields = [] as Array<string>;

  // Group all field values by year
  for (const pair of formData.entries()) {
    // The field are named following this pattern: {year}-{turnover | employees}
    const [year, key] = pair[0].split("-") as [
      string,
      "employees" | "turnover",
    ]; // key is "turnover" or "employees";
    const value = Number(pair[1]);

    if (!stepsData[year]) {
      stepsData[year] = {};
      stepsData[year]["year"] = Number(year);
      // ex: {2022: {year: 2022}}
    }
    stepsData[year][key] = value; // ex: {2022: {year: 2022, employees: 100}}

    // Remember that an employee or turnover value is zero
    if (!value && !zeroValueFields.includes(key)) {
      zeroValueFields.push(key); // ex: ["employee"]
    }
  }

  // Return an error to indicate that an employee and/or turnover value is zero
  if (zeroValueFields.length) {
    return { error: { zeroValueFields } };
  }

  // Update the trajectory steps
  const response = await fetch("/api/client/trajectory_steps", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data: Object.values(stepsData) }),
  });

  if (response.status !== 200) {
    return { error: { undefined: true } };
  }

  // Redirect to next tab (when creating trajectory)
  if (isTrajectoryCreation === "true") {
    return redirect("/trajectory/creation/summary");
  }

  return { ok: true };
}

function TrajectoryProjections() {
  const { trackingCampaign, trajectory, trajectorySteps } =
    useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const { t } = useTranslation(["common", "trajectory"]);
  const navigate = useNavigate();
  const submit = useSubmit();
  const navigation = useNavigation();

  const location = useLocation();
  const isTrajectoryCreation = location.pathname.includes("/creation");

  invariant(
    trackingCampaign && trajectory && trajectorySteps,
    "Unable to access information needed to display projection assumptions."
  );

  const [isReadOnly, setIsReadOnly] = useState(!isTrajectoryCreation);
  const [formHasChanged, setFormHasChanged] = useState(false);

  const showNavigation = isTrajectoryCreation;
  const isFullWidth = isTrajectoryCreation;

  invariant(
    trackingCampaign && trajectory && trajectorySteps,
    "Unable to access information needed to display projection assumptions."
  );

  // Calculate the early projection year
  const earlyProjectionYear = getEarlyProjectionYear(
    trajectorySteps,
    trackingCampaign
  );

  invariant(
    earlyProjectionYear <= LATE_PROJECTION_YEAR,
    "The early projection year must be before the late projection year."
  );

  const defaultProjectionYear = getDefaultProjectionYear(
    earlyProjectionYear,
    LATE_PROJECTION_YEAR,
    trajectorySteps
  );

  const [projectionYear, setProjectionYear] = useState(defaultProjectionYear);

  function handleSubmit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>) {
    // Let's prevent the default event
    event.preventDefault();

    // Avoid requesting the API if the form has not changed (no modifications)
    if (isTrajectoryCreation && !formHasChanged && trajectorySteps?.length) {
      return navigate("/trajectory/creation/summary");
    }

    // Grab the form element
    const form = event.currentTarget;

    // Get the formData from that form
    const formData = new FormData(form);
    formData.set("isTrajectoryCreation", isTrajectoryCreation.toString());

    // Submit the form data, re-using the method and action from the form
    submit(formData, { method: "PUT" });
  }

  useEffect(() => {
    // Go into read-only mode except if the action failed
    if (!isTrajectoryCreation && !actionData?.error) {
      setIsReadOnly(true);
    }
  }, [actionData, isTrajectoryCreation]);

  return (
    <CardBackground>
      <Form
        method="PUT"
        onSubmit={handleSubmit}
        onChange={() => {
          if (!formHasChanged) {
            setFormHasChanged(true);
          }
        }}
      >
        <div className="flex items-center justify-between">
          <Typography as="h1" step="2" weight="bold">
            {t("trajectory:projections.title")}
          </Typography>
          {!isTrajectoryCreation && (
            <>
              {isReadOnly ? (
                <Button // A button to toggle isReadOnly state (do not trigger the form)
                  key="disable-edit-mode"
                  type="button"
                  intent="secondary"
                  shape="soft"
                  size="small"
                  PrefixIcon={Edit2}
                  onClick={() => setIsReadOnly(false)}
                >
                  {t("common:modify")}
                </Button>
              ) : (
                <Button // A button to toggle isReadOnly state and submit the form
                  key="submit-form"
                  type="submit"
                  intent="secondary"
                  shape="soft"
                  size="small"
                  PrefixIcon={Check}
                  isLoading={navigation.state !== "idle"}
                >
                  {t("common:save")}
                </Button>
              )}
            </>
          )}
        </div>
        <Typography className="my-xs">
          {isTrajectoryCreation
            ? t("trajectory:projections.text", {
                earlyProjectionYear,
                lateProjectionYear: LATE_PROJECTION_YEAR,
              })
            : t("trajectory:projections.text2")}
        </Typography>
        <div className={isFullWidth ? "w-full" : "w-full lg:w-3/4 xl:w-1/2"}>
          {(isTrajectoryCreation || !isReadOnly) && (
            <Radio
              className="gap-x-md grid grid-cols-2"
              label={t("trajectory:projections.lastYearOfProjection")}
              orientation="horizontal"
              type="button"
              size="medium"
              disabled={isReadOnly}
              items={[
                {
                  label: earlyProjectionYear.toString(),
                  value: earlyProjectionYear.toString(),
                },
                {
                  label: LATE_PROJECTION_YEAR.toString(),
                  value: LATE_PROJECTION_YEAR.toString(),
                },
              ]}
              onValueChange={(value) => setProjectionYear(Number(value))}
              defaultValue={projectionYear.toString()}
            />
          )}
          <ProjectionAssumptions
            trajectorySteps={trajectorySteps}
            trackingCampaign={trackingCampaign}
            defaultProjectionYear={defaultProjectionYear}
            projectionYear={projectionYear}
            readOnly={isReadOnly}
          />
          {actionData?.error?.zeroValueFields && (
            <ul>
              {actionData?.error?.zeroValueFields.map((zeroValueField) => (
                <Typography
                  as="li"
                  key={zeroValueField}
                  className="text-negative"
                >
                  {t(
                    `trajectory:projections.errors.zeroValue.${zeroValueField}`
                  )}
                </Typography>
              ))}
            </ul>
          )}
          {showNavigation && (
            <div className="mt-xl gap-x-md flex justify-center">
              <Button
                as={Link}
                to="/trajectory/creation/context"
                shape="outline"
              >
                {t("common:back")}
              </Button>
              <Button type="submit" isLoading={navigation.state !== "idle"}>
                {t("common:continue")}
              </Button>
            </div>
          )}
        </div>
      </Form>
    </CardBackground>
  );
}

export default TrajectoryProjections;
